import React from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import aboutHeroImage from '../../assets/pages-hero/about.png';

const About = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();

  const timeline = [
    { year: '2009', event: language === 'tr' ? "Türk Konseyi'ni kuran Nahçıvan Anlaşması imzalandı" : 'Nakhchivan Agreement signed, establishing the Turkic Council' },
    { year: '2010', event: language === 'tr' ? "İlk Zirve İstanbul'da (Türkiye) düzenlendi" : 'First Summit held in Istanbul, Türkiye' },
    { year: '2014', event: language === 'tr' ? "Sekretarya İstanbul'da kuruldu" : 'Secretariat established in Istanbul' },
    { year: '2018', event: language === 'tr' ? "Macaristan'a Gözlemci statüsü verildi" : 'Hungary granted Observer status' },
    { year: '2021', event: language === 'tr' ? 'Kuruluş, Türk Devletleri Teşkilatı (TDT) adını aldı' : 'Renamed to Organization of Turkic States (OTS)' },
    { year: '2022', event: language === 'tr' ? "Türkmenistan'a Gözlemci statüsü verildi" : 'Turkmenistan granted Observer status' },
  ];

  const objectives = [
    { icon: '🎯', title: language === 'tr' ? 'Birliğin Güçlendirilmesi' : 'Strengthening Unity', description: language === 'tr' ? 'Türk dili konuşan ülkeler arasında dayanışma ve karşılıklı güvenin geliştirilmesi.' : 'Fostering solidarity and mutual trust among Turkic-speaking nations.' },
    { icon: '🌐', title: language === 'tr' ? 'Bölgesel İş Birliği' : 'Regional Cooperation', description: language === 'tr' ? 'Bölgede barış, istikrar ve güvenliğin desteklenmesi.' : 'Promoting peace, stability, and security in the region.' },
    { icon: '📈', title: language === 'tr' ? 'Ekonomik Kalkınma' : 'Economic Development', description: language === 'tr' ? 'Ticaret, yatırım ve ekonomik koridorların geliştirilmesi.' : 'Enhancing trade, investment, and economic corridors.' },
    { icon: '🎓', title: language === 'tr' ? 'Eğitim ve Bilim' : 'Education & Science', description: language === 'tr' ? 'Eğitim ve bilim alanlarında iş birliğinin ilerletilmesi.' : 'Advancing educational and scientific collaboration.' },
    { icon: '🏛️', title: language === 'tr' ? 'Kültürel Mirasın Korunması' : 'Cultural Preservation', description: language === 'tr' ? 'Ortak Türk mirasının korunması ve tanıtılması.' : 'Protecting and promoting shared Turkic heritage.' },
    { icon: '🚀', title: language === 'tr' ? 'İnovasyon' : 'Innovation', description: language === 'tr' ? 'Teknoloji ve dijital dönüşümün benimsenmesi.' : 'Embracing technology and digital transformation.' },
  ];

  const leaders = [
    { name: language === 'tr' ? 'Genel Sekreter' : 'Secretary General', title: 'Kubanychbek Omuraliev', country: FLAGS.kyrgyzstan },
    { name: language === 'tr' ? 'Konsey Başkanı' : 'President of Council', title: language === 'tr' ? 'Dönem Başkanlığı' : 'Rotating Presidency', country: null },
  ];

  const memberFlags = [
    { name: 'Türkiye', flag: FLAGS.turkey },
    { name: language === 'tr' ? 'Azerbaycan' : 'Azerbaijan', flag: FLAGS.azerbaijan },
    { name: language === 'tr' ? 'Kazakistan' : 'Kazakhstan', flag: FLAGS.kazakhstan },
    { name: language === 'tr' ? 'Kırgızistan' : 'Kyrgyzstan', flag: FLAGS.kyrgyzstan },
    { name: language === 'tr' ? 'Özbekistan' : 'Uzbekistan', flag: FLAGS.uzbekistan },
  ];

  return (
    <div style={styles.page}>
      {/* Hero Section */}
      <section style={{
        ...styles.hero,
        background: isDarkMode 
          ? `linear-gradient(135deg, ${DARK_THEME.background}DD 0%, ${DARK_THEME.backgroundTertiary}DD 100%), url(${aboutHeroImage})`
          : `linear-gradient(135deg, ${COLORS.primary}DD 0%, ${COLORS.dark}DD 100%), url(${aboutHeroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'multiply',
      }}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : `${COLORS.primary}20`,
            color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
          }}>{language === 'tr' ? 'Hakkımızda' : 'About Us'}</span>
          <h1 style={styles.heroTitle}>{language === 'tr' ? 'Türk Devletleri Teşkilatı' : 'Organization of Turkic States'}</h1>
          <p style={styles.heroSubtitle}>
            {language === 'tr'
              ? 'Ortak miras ve ortak vizyonla uluslar arasında köprüler kuruyoruz'
              : 'Building bridges between nations through shared heritage and common vision'}
          </p>
          <div style={styles.heroFlags}>
            {memberFlags.map((item, index) => (
              <img key={index} src={item.flag} alt={item.name} style={styles.heroFlagImage} />
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section style={{
        ...styles.section,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={styles.container}>
          <div style={styles.missionGrid}>
            <div style={styles.missionContent}>
              <h2 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                {language === 'tr' ? 'Misyonumuz' : 'Our Mission'}
              </h2>
              <p style={{...styles.missionText, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                {language === 'tr'
                  ? 'Türk Devletleri Teşkilatı (TDT), Türk devletlerini bir araya getiren uluslararası bir hükümetler arası kuruluştur. Misyonumuz; üye devletler arasında kapsamlı iş birliğini teşvik etmek, barış ve istikrarı güçlendirmek, ekonomik ve kültürel bağları ilerletmektir.'
                  : 'The Organization of Turkic States (OTS) is an international intergovernmental organization comprising Turkic countries. Our mission is to promote comprehensive cooperation among member states, strengthen peace and stability, and advance economic and cultural ties.'}
              </p>
              <p style={{...styles.missionText, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                {language === 'tr'
                  ? 'Ulusların egemenliğe saygı temelinde eşit koşullarda iş birliği yaptığı, modern İpek Yolu boyunca ortak refah ve kalkınma hedefleri doğrultusunda çalışan birleşik bir Türk dünyası öngörüyoruz.'
                  : 'We envision a united Turkic world where nations collaborate on equal footing, respecting sovereignty while working towards common goals of prosperity and development along the modern Silk Road.'}
              </p>
            </div>
            <div style={styles.missionStats}>
              { [
                { num: '5', label: language === 'tr' ? 'Üye Devlet' : 'Member States' },
                { num: '3', label: language === 'tr' ? 'Gözlemci' : 'Observers' },
                { num: '170M+', label: language === 'tr' ? 'Nüfus' : 'Population' },
                { num: '$1.5T', label: language === 'tr' ? 'Toplam GSYİH' : 'Combined GDP' },
              ].map((stat, index) => (
                <div key={index} style={{
                  ...styles.statBox,
                  background: isDarkMode 
                    ? `linear-gradient(135deg, ${DARK_THEME.primary} 0%, ${DARK_THEME.backgroundTertiary} 100%)`
                    : COLORS.primaryGradient,
                }}>
                  <span style={styles.statNumber}>{stat.num}</span>
                  <span style={styles.statLabel}>{stat.label}</span>
                </div>
                ))} 

            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section style={{
        ...styles.sectionAlt,
        backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
      }}>
        <div style={styles.container}>
          <div style={styles.header}>
            <span style={{
              ...styles.badgeSecondary,
              backgroundColor: isDarkMode ? `${DARK_THEME.secondary}30` : `${COLORS.secondary}20`,
              color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary,
            }}>{language === 'tr' ? 'Neler Yapıyoruz' : 'What We Do'}</span>
            <h2 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
              {language === 'tr' ? 'Hedeflerimiz' : 'Our Objectives'}
            </h2>
          </div>
          <div style={styles.objectivesGrid}>
            {objectives.map((obj, index) => (
              <div key={index} style={{
                ...styles.objectiveCard,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.06)',
              }}>
                <span style={styles.objectiveIcon}>{obj.icon}</span>
                <h3 style={{...styles.objectiveTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                  {obj.title}
                </h3>
                <p style={{...styles.objectiveDesc, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {obj.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section style={{
        ...styles.section,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={styles.container}>
          <div style={styles.header}>
            <span style={{
              ...styles.badge,
              backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : `${COLORS.primary}20`,
              color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
            }}>{language === 'tr' ? 'Yolculuğumuz' : 'Our Journey'}</span>
            <h2 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
              {language === 'tr' ? 'Tarihçe ve Kilometre Taşları' : 'History & Milestones'}
            </h2>
          </div>
          <div style={styles.timeline}>
            {timeline.map((item, index) => (
              <div key={index} style={styles.timelineItem}>
                <div style={{...styles.timelineYear, color: isDarkMode ? DARK_THEME.primary : COLORS.primary}}>
                  {item.year}
                </div>
                <div style={{
                  ...styles.timelineDot,
                  backgroundColor: isDarkMode ? DARK_THEME.primary : COLORS.primary,
                  border: `4px solid ${isDarkMode ? DARK_THEME.backgroundTertiary : COLORS.primaryLight}`,
                }}></div>
                <div style={{
                  ...styles.timelineContent,
                  backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light,
                  borderLeft: `3px solid ${isDarkMode ? DARK_THEME.primary : COLORS.primary}`,
                }}>
                  <p style={{...styles.timelineText, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>
                    {item.event}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section style={{
        ...styles.sectionDark,
        background: isDarkMode 
          ? `linear-gradient(180deg, ${DARK_THEME.backgroundTertiary} 0%, ${DARK_THEME.background} 100%)`
          : COLORS.darkGradient,
      }}>
        <div style={styles.container}>
          <div style={styles.header}>
            <span style={styles.badgeLight}>{language === 'tr' ? 'Liderlik' : 'Leadership'}</span>
            <h2 style={styles.sectionTitleLight}>{language === 'tr' ? 'Liderlik Kadromuz' : 'Our Leadership'}</h2>
          </div>
          <div style={styles.leadersGrid}>
            {leaders.map((leader, index) => (
              <div key={index} style={{
                ...styles.leaderCard,
                backgroundColor: isDarkMode ? DARK_THEME.surface : 'rgba(255,255,255,0.08)',
                border: `1px solid ${isDarkMode ? DARK_THEME.border : 'rgba(255,255,255,0.1)'}`,
              }}>
                {leader.country ? (
                  <img src={leader.country} alt="" style={styles.leaderFlagImg} />
                ) : (
                  <span style={styles.leaderFlag}>🏛️</span>
                )}
                <h3 style={styles.leaderName}>{leader.title}</h3>
                <p style={{...styles.leaderTitle, color: isDarkMode ? DARK_THEME.primary : COLORS.primary}}>
                  {leader.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { paddingTop: '0px' },
  hero: {
    height: '50vh',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'rgba(255,255,255,0.03)\'/%3E%3C/svg%3E")',
  },
  heroContent: { position: 'relative', zIndex: 2, padding: '0 20px' },
  badge: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  badgeSecondary: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  badgeLight: {
    display: 'inline-block',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: COLORS.white,
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  heroTitle: {
    fontSize: 'clamp(32px, 6vw, 56px)',
    color: COLORS.white,
    fontWeight: '700',
    marginBottom: '20px',
  },
  heroSubtitle: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.8)',
    maxWidth: '600px',
    margin: '0 auto 30px',
  },
  heroFlags: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    flexWrap: 'wrap',
  },
  heroFlagImage: {
    height: '36px',
    width: 'auto',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  section: { padding: '100px 20px' },
  sectionAlt: { padding: '100px 20px' },
  sectionDark: { padding: '100px 20px' },
  container: { maxWidth: '1200px', margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: '60px' },
  sectionTitle: { fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: '700' },
  sectionTitleLight: { fontSize: 'clamp(28px, 4vw, 42px)', color: COLORS.white, fontWeight: '700' },
  missionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '60px',
    alignItems: 'center',
  },
  missionContent: {},
  missionText: { fontSize: '17px', lineHeight: '1.8', marginBottom: '20px' },
  missionStats: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' },
  statBox: { borderRadius: '16px', padding: '30px 20px', textAlign: 'center' },
  statNumber: { display: 'block', fontSize: '36px', fontWeight: '700', color: COLORS.white, marginBottom: '8px' },
  statLabel: { fontSize: '14px', color: 'rgba(255,255,255,0.8)' },
  objectivesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px' },
  objectiveCard: { padding: '35px 25px', borderRadius: '16px', textAlign: 'center', transition: 'transform 0.3s ease' },
  objectiveIcon: { fontSize: '42px', marginBottom: '20px', display: 'block' },
  objectiveTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '12px' },
  objectiveDesc: { fontSize: '14px', lineHeight: '1.6' },
  timeline: { position: 'relative', maxWidth: '800px', margin: '0 auto' },
  timelineItem: { display: 'flex', alignItems: 'flex-start', gap: '30px', marginBottom: '40px', position: 'relative' },
  timelineYear: { minWidth: '80px', fontSize: '18px', fontWeight: '700', textAlign: 'right' },
  timelineDot: { width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, marginTop: '4px' },
  timelineContent: { flex: 1, padding: '20px 25px', borderRadius: '12px' },
  timelineText: { fontSize: '15px', lineHeight: '1.6', margin: 0 },
  leadersGrid: { display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' },
  leaderCard: { padding: '40px 50px', borderRadius: '20px', textAlign: 'center' },
  leaderFlag: { fontSize: '48px', marginBottom: '20px', display: 'block' },
  leaderFlagImg: { height: '48px', width: 'auto', marginBottom: '20px', borderRadius: '6px' },
  leaderName: { fontSize: '20px', color: COLORS.white, fontWeight: '600', marginBottom: '8px' },
  leaderTitle: { fontSize: '14px' },
};

export default About;
